<template>
  <div class="securpos">
    <LoadingSpinner :isLoading="isLoading" />
    <div class="min-h-screen bg-primary-300 bg-opacity-50 flex items-center justify-center">
      <div class="bg-white w-full md:w-1/2 lg:w-1/3 p-8 md:p-12 mx-8 rounded-lg shadow-xl">
        <div>
          <img class="logo w-32 mx-auto" :src="logoSrc">
        </div>
        <div class="mt-8">
          <!-- Login Section -->
          <main v-if="!isLoggedIn">
            <section class="mt-8">
              <ValidationObserver ref="formLoginSecurPos">
                <div class="flex flex-col">
                  <div class="w-full">
                    <fieldset class="pb-4" v-if="showTelematicsOption">
                      <div class="flex justify-between items-center space-x-4">

                        <div class="flex items-center space-x-2">
                          <input id="telematics" class="peer/telematics" type="radio" name="status" value="telematics" v-model="selectedOption"/>
                          <label for="telematics" class="peer-checked/telematics:text-gray-700 text-sm font-sans">{{ t('telematics') }}</label>
                        </div>

                        <div class="flex items-center space-x-2">
                          <input id="inventory_number" class="peer/inventory_number" type="radio" name="status" value="inventory_number" v-model="selectedOption"/>
                          <label for="inventory_number" class="peer-checked/inventory_number:text-gray-700 text-sm font-sans">{{ t('inventory') }}</label>
                        </div>
                      </div>
                    </fieldset>
                    <input type="text" v-model="deviceNumber" class="py-3 pl-2 text-xs w-full bg-gray-100 rounded shadow" rules="required" v-on:keyup.enter="verifyDevice()" :placeholder="dynamicPlaceholder"/>
                    <span v-if="errorMessage" class="text-red-500 text-xs">{{ errorMessage }}</span>
                  </div>

                  <button class="btn-blue px-2 py-3 text-sm mt-5" @click.prevent="verifyDevice()">
                    {{ t('login') }}
                    <BaseIcon icon="key" class="ml-2" />
                  </button>
                </div>
              </ValidationObserver>

            </section>
          
          </main>

          <!-- Logged In Section -->
          <main v-if="isLoggedIn">
            <!-- Visit List View -->
            <section v-if="!showVisitForm" class="mt-8">
              <div class="flex flex-col">
                <div class="w-full">
                  <h2 class="text-lg font-bold text-gray-700 mb-4">{{ t('yourVisits') }}</h2>
                  
                  <!-- No visits message -->
                  <div v-if="visits.length === 0" class="text-center py-8 text-gray-500">
                    <BaseIcon icon="calendar-alt" class="text-4xl mb-2" />
                    <p>{{ t('noVisits') }}</p>
                  </div>

                  <!-- Visit cards -->
                  <div v-else class="space-y-3 max-h-96 overflow-y-auto">
                    <div v-for="visit in visits" :key="visit._id" class="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition">
                      <div class="flex justify-between items-start">
                        <div class="flex-1">
                          <div class="flex items-center mb-2">
                            <BaseIcon :icon="getVisitStatusIcon(visit)" :class="getVisitStatusColor(visit)" class="mr-2" />
                            <span class="text-xs font-semibold" :class="getVisitStatusColor(visit)">
                              {{ getVisitStatus(visit) }}
                            </span>
                          </div>
                          <div class="text-sm space-y-1">
                            <p><strong>{{ t('address') }}:</strong> {{ formatAddress(visit.address) }}</p>
                            <p><strong>{{ t('startTime') }}:</strong> {{ formatDate(visit.startTime) }}</p>
                            <p><strong>{{ t('duration') }}:</strong> {{ timeToName(visit.timeLength) }}</p>
                            <p><strong>{{ t('endTime') }}:</strong> {{ formatEndTime(visit) }}</p>
                          </div>
                        </div>
                      </div>
                      <div class="mt-3 flex space-x-2">
                        <button class="btn-green-outline text-xs px-3 py-1" @click="editVisit(visit)">
                          <BaseIcon icon="edit" class="mr-1" /> {{ t('edit') }}
                        </button>
                        <button class="btn-red-outline text-xs px-3 py-1" @click="cancelVisitPrompt(visit)">
                          <BaseIcon icon="trash" class="mr-1" /> {{ t('delete') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border-b border-secondary-400 my-5"></div>

                <button class="btn-blue px-2 py-3 text-sm" @click="startNewVisit()">
                  {{ t('newVisit') }}
                  <BaseIcon icon="plus" class="ml-2" />
                </button>

                <button class="btn-gray px-2 py-3 text-sm mt-2" @click="logout()">
                  {{ t('logout') }}
                  <BaseIcon icon="sign-out-alt" class="ml-2" />
                </button>
              </div>
            </section>

            <!-- Visit Form (New or Edit) -->
            <section v-if="showVisitForm" class="mt-8">
              <h2 class="text-lg font-bold text-gray-700 mb-4">
                {{ editingVisit ? t('editVisitTitle') : t('newVisitTitle') }}
              </h2>
              <ValidationObserver ref="formVisitSecurPos">
                <div class="flex flex-col">
                  <div class="telematics" v-if="this.selectedOption === 'telematics'">
                    <div class="w-full py-1">
                      <BaseInput v-model="address.area" type="text" maxlength="50" :field_name="t('address')" />
                    </div>
                    <div class="w-full py-1">
                      <BaseInput v-model.trim="address.floor" type="number" :field_name="t('floor')" />
                    </div>
                    <div class="w-full py-1">
                      <BaseInput v-model="address.building" type="text" maxlength="20" :field_name="t('apartmentName')" />
                    </div>
                    <div class="w-full py-1">
                      <BaseInput v-model="address.visitor" type="text" maxlength="50" :field_name="t('user')" />
                    </div>
                  </div>

                  <div class="inventory_number" v-if="this.selectedOption === 'inventory_number'">
                    <div class="w-full py-1">
                      <BaseInput v-model="address.area" type="text" maxlength="150" :field_name="t('address')" :placeholder="t('addressHint')" />
                    </div>
                    <div class="w-full py-1">
                      <BaseInput v-model="address.floor" type="text" maxlength="150" :field_name="t('userNamePhone')" :placeholder="t('userNamePhoneHint')" />
                    </div>
                    <div class="w-full py-1">
                      <BaseInput v-model="address.building" type="text" maxlength="150" :field_name="t('signalement')" :placeholder="t('signalementHint')" />
                    </div>
                    <div class="w-full py-1">
                      <BaseInput v-model="address.visitor" type="text" maxlength="150" :field_name="t('otherInfo')" :placeholder="t('otherInfoHint')" class="h-20"/>
                    </div>
                  </div>
               

                  <div class="border-b border-secondary-400 my-5"></div>

                  <div class="w-full py-1">
                    <label class="text-gray-600 text-sm font-bold font-serif">{{ t('timerLength') }}<span class="text-gray-400 text-xs font-normal ml-2">{{ t('timerLengthHint') }}</span></label>
                    <div class="select-wrapper mt-2">
                      <select v-model="timeLength" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-1 py-1">
                        <option v-for="o in timeOptions" :key="o.value" :value="o.value">{{ o.name }}</option>
                      </select>
                    </div>
                    <p class="text-red-600 text-xs font-bold mt-2">{{ t('timerWarning') }}</p>
                  </div>

                  <div class="w-full py-1">
                    <label class="text-gray-600 text-sm font-bold font-serif">{{ t('startDateTime') }}<span class="text-gray-400 text-xs font-normal ml-2">{{ t('startDateTimeHint') }}</span></label>
                    <div class="datepicker-wrapper mt-2">
                      <date-picker v-model="startTime" type="datetime" format="YYYY-MM-DD HH:mm" :default-value="new Date()" :show-second="false" :editable="false" :clearable="false" :placeholder="t('chooseDateTime')" :lang="datepickerLang">
                      </date-picker>
                    </div>
                  </div>

                  <button class="btn-blue px-2 py-3 text-sm mt-8" @click.prevent="saveVisit()">
                    {{ editingVisit ? t('saveChanges') : t('startTimer') }}
                    <BaseIcon :icon="editingVisit ? 'save' : 'hourglass-start'" class="ml-2" />
                  </button>

                  <button class="btn-gray px-2 py-3 text-sm mt-2" @click.prevent="cancelForm()">
                    {{ t('cancel') }}
                    <BaseIcon icon="times" class="ml-2" />
                  </button>
                </div>
              </ValidationObserver>
            </section>
          </main>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/sv';
import 'vue2-datepicker/locale/nb';
import 'vue2-datepicker/index.css';
import { setValidationLocale } from '@/utils/input_validation';

export default {
  name: 'SecurPos',
  title () {
    return `SecurPos`;
  },
  components: {
    DatePicker
  },
  data() {
    return {
      isLoading: false,
      isLoggedIn: false,
      deviceNumber: '',
      errorMessage: '',
      selectedOption: this.getDefaultOption(), 
      timeLength: 1800,
      showVisitForm: false,
      editingVisit: null,
      visits: [],
      isMalmoSubdomain: false,
      currentLang: 'sv',
      translations: {
        sv: {
          telematics: 'Telematiknummer',
          inventory: 'Inventarienummer',
          enterNumber: 'Ange nummer',
          required: 'Fältet är obligatoriskt',
          exact6: 'Numret måste vara exakt 6 siffror.',
          login: 'Logga in',
          yourVisits: 'Dina besök',
          noVisits: 'Du har inga aktiva eller kommande besök',
          address: 'Adress',
          floor: 'Våning',
          startTime: 'Starttid',
          duration: 'Längd',
          endTime: 'Sluttid',
          edit: 'Ändra',
          delete: 'Ta bort',
          newVisit: 'Registrera nytt besök',
          logout: 'Logga ut',
          newVisitTitle: 'Nytt besök',
          editVisitTitle: 'Ändra besök',
          apartmentName: 'Namn på LGH',
          user: 'Användare',
          userNamePhone: 'Namn och telefon till användare',
          signalement: 'Signalement',
          otherInfo: 'Relevant information',
          timerLength: 'Timerlängd',
          startDateTime: 'Startdatum och tid',
          addressHint: 'Besöksadress, lägenhetsnummer, våning eller verksamhet',
          userNamePhoneHint: 'Namn och telefonnummer till den som bär larmet',
          signalementHint: 'Medarbetarens kännetecken, t.ex. hårfärg, glasögon eller klädsel – inte brukarens',
          otherInfoHint: 'T.ex. fordon, reg.nr, portkod samt antal medarbetare och brukare',
          timerLengthHint: 'Välj beräknad längd på besöket',
          timerWarning: 'OBS! Tänk på att Larmcentralens operatörer INTE kan se besöksinformationen efter tiden har löpt ut.',
          startDateTimeHint: 'Välj datum och tid för besökets start',
          chooseDateTime: 'Välj datum och tid',
          startTimer: 'Starta säkerhetstimern',
          saveChanges: 'Spara ändringar',
          cancel: 'Avbryt',
          minutes: 'minuter',
          hours: 'timmar',
          hour: 'timme',
          upcoming: 'Kommande',
          ongoing: 'Pågående',
          ended: 'Avslutad',
          visitUpdated: 'Besöket har uppdaterats',
          visitCreated: 'Besöket har skapats',
          visitDeleted: 'Besöket har tagits bort',
          deletePrompt: 'Ta bort ditt besök den',
          yes: 'JA',
          no: 'NEJ'
        },
        no: {
          telematics: 'Telematikknr',
          inventory: 'Inventarnr',
          enterNumber: 'Angi nummer',
          required: 'Feltet er obligatorisk',
          exact6: 'Nummeret må være nøyaktig 6 sifre.',
          login: 'Logg inn',
          yourVisits: 'Dine besøk',
          noVisits: 'Du har ingen aktive eller kommende besøk',
          address: 'Adresse',
          floor: 'Etasje',
          startTime: 'Starttid',
          duration: 'Varighet',
          endTime: 'Sluttid',
          edit: 'Endre',
          delete: 'Slett',
          newVisit: 'Registrer nytt besøk',
          logout: 'Logg ut',
          newVisitTitle: 'Nytt besøk',
          editVisitTitle: 'Endre besøk',
          apartmentName: 'Navn på leilighet',
          user: 'Bruker',
          userNamePhone: 'Navn og telefon til bruker',
          signalement: 'Signalement',
          otherInfo: 'Øvrig informasjon',
          timerLength: 'Timerlengde',
          startDateTime: 'Startdato og tid',
          addressHint: 'Besøksadresse, leilighetsnummer, etasje eller virksomhet',
          userNamePhoneHint: 'Navn og telefonnummer til den som bærer alarmen',
          signalementHint: 'Medarbeiderens kjennetegn, f.eks. hårfarge, briller eller klær – ikke brukerens',
          otherInfoHint: 'F.eks. kjøretøy, reg.nr, portkode samt antall medarbeidere og brukere',
          timerLengthHint: 'Velg beregnet lengde på besøket',
          timerWarning: 'OBS! Husk at alarmsentralens operatører IKKE kan se besøksinformasjonen etter at tiden har løpt ut.',
          startDateTimeHint: 'Velg dato og tid for besøkets start',
          chooseDateTime: 'Velg dato og tid',
          startTimer: 'Start sikkerhetstimeren',
          saveChanges: 'Lagre endringer',
          cancel: 'Avbryt',
          minutes: 'minutter',
          hours: 'timer',
          hour: 'time',
          upcoming: 'Kommende',
          ongoing: 'Pågående',
          ended: 'Avsluttet',
          visitUpdated: 'Besøket er oppdatert',
          visitCreated: 'Besøket er opprettet',
          visitDeleted: 'Besøket er slettet',
          deletePrompt: 'Slett besøket ditt den',
          yes: 'JA',
          no: 'NEI'
        }
      },
      timeOptions: [
        {  name: '15 minuter', value: 60*15 },
        {  name: '30 minuter', value: 60*30 },
        {  name: '1 timme', value: 60*60 },
        {  name: '2 timmar', value: 60*60*2 },
        {  name: '3 timmar', value: 60*60*3 },
        {  name: '4 timmar', value: 60*60*4 },
        {  name: '6 timmar', value: 60*60*6 },
        {  name: '8 timmar', value: 60*60*8 },
        {  name: '10 timmar', value: 60*60*10 },
        {  name: '12 timmar', value: 60*60*12 },
        {  name: '18 timmar', value: 60*60*18 },
        {  name: '24 timmar', value: 60*60*24 },
        {  name: '36 timmar', value: 60*60*36 },
        {  name: '48 timmar', value: 60*60*48 },
        {  name: '72 timmar', value: 60*60*72 },
      ],
      address: {
        area: '',
        floor: '',
        building: '',
        visitor: ''
      },
      startTime: null,
    }
  },

  created() {
    // Detect if user is on malmo.securdia.se subdomain
    const hostname = window.location.hostname;
    this.isMalmoSubdomain = hostname === 'malmo.securdia.se' || hostname === 'localhost'; // localhost for testing
    
    // Detect language
    if (hostname === 'securpos.securdia.no') {
      this.currentLang = 'no';
      this.moment.locale('nb');
      setValidationLocale('nb');
    } else {
      this.currentLang = 'sv';
      this.moment.locale('sv');
      setValidationLocale('sv');
    }
    
    // Update time options with correct language
    this.updateTimeOptions();
  },

  computed: {
    logoSrc() {
      if (this.isMalmoSubdomain) {
        return require('@/assets/logos/MmoLogo.jpg');
      }
      return require('@/assets/logos/logo_horizontal.png');
    },
    dynamicPlaceholder() {
      return this.selectedOption === 'inventory_number'
        ? this.t('inventory')
        : this.selectedOption === 'telematics'
        ? this.t('telematics')
        : this.t('enterNumber');
    },
    
    showTelematicsOption() {
      // Hide telematics option for malmo.securdia.se
      return !this.isMalmoSubdomain;
    },

    datepickerLang() {
      return this.currentLang === 'no' ? 'nb' : 'sv';
    }
  },

  methods: {
    
    t(key) {
      return this.translations[this.currentLang][key] || key;
    },

    translateBackendMessage(msg) {
      if (this.currentLang !== 'no') return msg;
      
      const map = {
        'Det gick inte att hitta enheten': 'Enheten ble ikke funnet',
        'SecurPos aktiveras inte': 'SecurPos aktiveres ikke',
        'Ett besök existerar redan inom denna tidsperiod': 'Et besøk eksisterer allerede i denne perioden',
        'Besöket hittades inte': 'Besøket ble ikke funnet'
      };
      
      return map[msg] || msg;
    },

    updateTimeOptions() {
      const min = this.t('minutes');
      const hour = this.t('hour');
      const hours = this.t('hours');
      
      this.timeOptions = [
        {  name: `15 ${min}`, value: 60*15 },
        {  name: `30 ${min}`, value: 60*30 },
        {  name: `1 ${hour}`, value: 60*60 },
        {  name: `2 ${hours}`, value: 60*60*2 },
        {  name: `3 ${hours}`, value: 60*60*3 },
        {  name: `4 ${hours}`, value: 60*60*4 },
        {  name: `6 ${hours}`, value: 60*60*6 },
        {  name: `8 ${hours}`, value: 60*60*8 },
        {  name: `10 ${hours}`, value: 60*60*10 },
        {  name: `12 ${hours}`, value: 60*60*12 },
        {  name: `18 ${hours}`, value: 60*60*18 },
        {  name: `24 ${hours}`, value: 60*60*24 },
        {  name: `36 ${hours}`, value: 60*60*36 },
        {  name: `48 ${hours}`, value: 60*60*48 },
        {  name: `72 ${hours}`, value: 60*60*72 },
      ];
    },

    getDefaultOption() {
      // Default to inventory_number for malmo.securdia.se, otherwise telematics
      const hostname = window.location.hostname;
      return (hostname === 'malmo.securdia.se' || hostname === 'localhost') ? 'inventory_number' : 'telematics';
    },
    
    async verifyDevice() {
      this.errorMessage = ''; 
      if (!this.deviceNumber.trim()) {
        this.errorMessage = this.t('required');
        return;
      }
      if (this.selectedOption === "inventory_number") {
        if (!/^\d{6}$/.test(this.deviceNumber)) {
          this.errorMessage = this.t('exact6');
          return;
        }
      }
      this.errorMessage = ""; 


      this.$refs.formLoginSecurPos.validate().then(async (success) => {
        if (!success) {
          return;
        }

        try {
          let type = this.selectedOption;
          this.isLoading = true;
          let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/securpos/verify/${this.deviceNumber}/${type}`);
          this.isLoading = false;
          
          if(response.data.error) this.handleError(null, this.translateBackendMessage(response.data.message));
          if(response.data.success) {
            this.deviceNumber = response.data.phone;
            this.isLoggedIn = true;
            await this.loadVisits();
          }

        } catch (error) {
          this.isLoading = false;
          this.isLoggedIn = false;
          this.visits = [];

          this.handleError(error);
        }
      });
    },

    async loadVisits() {
      try {
        this.isLoading = true;
        let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/securpos/device/visits/${this.deviceNumber}`);
        this.isLoading = false;
        
        if(response.data.success) {
          this.visits = response.data.visits;
        }
      } catch (error) {
        this.isLoading = false;
        this.handleError(error);
      }
    },

    startNewVisit() {
      this.editingVisit = null;
      this.resetForm();
      this.showVisitForm = true;
    },

    editVisit(visit) {
      this.editingVisit = visit;
      this.address = {
        area: visit.address.area,
        floor: visit.address.floor,
        building: visit.address.building,
        visitor: visit.address.visitor
      };
      this.startTime = new Date(visit.startTime);
      this.timeLength = visit.timeLength;
      this.showVisitForm = true;
    },

    async saveVisit() {
      this.$refs.formVisitSecurPos.validate().then(async (success) => {
        if (!success) {
          return;
        }

        try {
          let data = {
            address: this.address,
            startTime: this.startTime,
            timeLength: this.timeLength,
          };

          this.isLoading = true;
          let response;

          if (this.editingVisit) {
            // Update existing visit
            response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/securpos/visit/${this.editingVisit._id}`, data);
          } else {
            // Create new visit
            data.devicePhone = this.deviceNumber;
            response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/securpos/visit/new`, data);
          }
          
          this.isLoading = false;
          
          if(response.data.error) {
            this.handleError(null, this.translateBackendMessage(response.data.message));
          } else if(response.data.success) {
            this.handleSuccess(this.editingVisit ? this.t('visitUpdated') : this.t('visitCreated'));
            this.showVisitForm = false;
            this.resetForm();
            await this.loadVisits();
          }

        } catch (error) {
          this.isLoading = false;
          this.handleError(error);
        }
      });
    },

    timeToName(value) {
      let t = _.find(this.timeOptions, {  value: value });
      if(t) return t.name;
      else return '';
    },

    cancelVisitPrompt(visit) {
      this.$modal.show('dialog', {
        title: `${this.t('deletePrompt')} <span class="text-red-500">${this.formatDate(visit.startTime)}</span>?`,
        buttons: [
          {
            title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.t('no')}</div>`,
            handler: () => {
              this.$modal.hide('dialog');
            }
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.t('yes')}</div>`,
            handler: () => {
              this.cancelVisit(visit._id);
            }
          }
        ]
      });
    },

    async cancelVisit(id) {
      try {
        this.isLoading = true;
        await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/securpos/visit/cancel/${id}`);
        this.isLoading = false;

        this.$modal.hide('dialog');
        this.handleSuccess(this.t('visitDeleted'));

        await this.loadVisits();

      } catch (error) {
        this.isLoading = false;
        this.$modal.hide('dialog');
        this.handleError(error);
      }
    },

    cancelForm() {
      this.showVisitForm = false;
      this.resetForm();
    },

    logout() {
      this.isLoggedIn = false;
      this.deviceNumber = '';
      this.visits = [];
      this.showVisitForm = false;
      this.resetForm();
    },

    resetForm() {
      this.address = {
        area: '',
        floor: '',
        building: '',
        visitor: ''
      };
      this.startTime = null;
      this.timeLength = 1800;
      this.editingVisit = null;
    },

    formatDate(date) {
      if(date) return this.moment(new Date(date)).format("YYYY-MM-DD HH:mm");
      else return '';
    },

    formatEndTime(visit) {
      if(visit && visit.startTime && visit.timeLength) {
        const endTime = this.moment(new Date(visit.startTime)).add(visit.timeLength, 'seconds');
        return endTime.format("YYYY-MM-DD HH:mm");
      }
      return '';
    },

    formatAddress(address) {
      if (!address) return '';
      const parts = [address.area, address.floor, address.building, address.visitor];
      return parts.filter(p => p && p.trim()).join(', ');
    },

    getVisitStatus(visit) {
      const now = this.moment();
      const startTime = this.moment(visit.startTime);
      const endTime = this.moment(visit.startTime).add(visit.timeLength, 'seconds');

      if (now.isBefore(startTime)) {
        return this.t('upcoming');
      } else if (now.isBetween(startTime, endTime)) {
        return this.t('ongoing');
      } else {
        return this.t('ended');
      }
    },

    getVisitStatusIcon(visit) {
      const status = this.getVisitStatus(visit);
      if (status === this.t('upcoming')) return 'clock';
      if (status === this.t('ongoing')) return 'circle';
      return 'check-circle';
    },

    getVisitStatusColor(visit) {
      const status = this.getVisitStatus(visit);
      if (status === this.t('upcoming')) return 'text-blue-500';
      if (status === this.t('ongoing')) return 'text-green-500';
      return 'text-gray-400';
    },

  }

}

</script>